import React, { useState, useEffect } from "react";
import {
    makeStyles,
    Typography,
    Paper,
    Grid,
    Button,
    Backdrop,
    Chip,
    darken,
    FormControl,
    InputLabel,
    IconButton,
    Select,
    MenuItem,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";

import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableHead from "@material-ui/core/TableHead";

import SearchComponent from "../../SearchComponent";
import { TablePaginationActions } from "../../TablePaginationActions";
import CreateItemModal from "./modals/ItemCategoryModals/CreateItemModal";
import EditItemModal from "./modals/ItemCategoryModals/EditItemModal";
import DeleteItemModal from "./modals/ItemCategoryModals/DeleteItemModal";
import { formatAmount } from "../../../utils";

import { getAllInventoryCategories, getInventoryCategoryItems } from "../../../actions/inventoryActions";
import { useDispatch, useSelector } from "react-redux";
import Feedback from "../../atoms/Feedback";

const useStyles = makeStyles((theme) => ({
    title: {
        display: "block",
        fontSize: 16,
        lineHeight: 1.5,
        letterSpacing: 1.5,
        fontFamily: "Rubik",
        fontWeight: "bold"
    },
    textField: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    paper: {
        padding: theme.spacing(3),
    },

    action: {
        marginBottom: 30,
    },
    btn: {
        marginTop: 30,
        marginBottom: 30,
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: "#fff",
    },
    table: {
        minWidth: 500,
    },
    chip: {
        paddingLeft: 8,
        paddingRight: 8,
    },
    statusDarkGreen: {
        borderColor: "#4caf50",
    },
    statusGreen: {
        borderColor: "#1de9b6",
    },
    statusYellow: {
        borderColor: "#fbc02d",
    },
    statusOrange: {
        borderColor: "#ff9800",
    },
    statusRed: {
        borderColor: "#ef5350",
    },
    marginBottom: {
        marginBottom: 30,
    },
    divider2: {
        height: 28,
        margin: 4,
    },
    createBtn: {
        letterSpacing: 2.5,
        padding: theme.spacing(1.2),
        paddingLeft: 20,
        paddingRight: 20,
    },
    createBtn2: {
        fontFamily: "Rubik",
        letterSpacing: 2.5,
        padding: theme.spacing(1.2),
        width: 200,
        marginLeft: 20,
        background: "#388e3c",
        color: "#fff",
        "&:hover": {
            background: darken("#388e3c", 0.15),
        },
    },
}));

const InventoryItems = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);

    const [id, setId] = useState("");
    const [category, setCategory] = useState("");
    const [search, setSearch] = useState("");

    const [feed, setFeed] = useState({
        loading: false,
        open: false,
        message: "",
        success: false,
    });

    const { loading, error, invCates, invCateItems } = useSelector((state) => state.inventoryItem);

    useEffect(() => {
        if (error && error !== null) {
            setFeed((prev) => ({
                loading: false,
                open: !prev.open,
                message: error,
                success: false,
            }));
        }
    }, [error]);

    useEffect(() => {
        dispatch(getAllInventoryCategories());
        if (category !== "") {
            dispatch(getInventoryCategoryItems(category));
        }
    }, [dispatch, category]);

    const updateSearch = (e) => {
        setSearch(e.target.value.substr(0, 20));
    };

    const ResetSearch = (e) => {
        e.preventDefault();
        setSearch("");
    };

    let filteredRows = () =>
        invCateItems === undefined
            ? []
            : invCateItems &&
            invCateItems.filter((row) => {
                if (search !== "") {
                    return (
                        row.name
                            .toString()
                            .toLowerCase()
                            .indexOf(search.toLowerCase()) !== -1
                    );
                } else {
                    return row;
                }
            });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handleChangeCategory = (cate) => {
        setCategory(cate.target.value);
    };
    const handleOpenCreate = () => {
        setOpenCreate(true);
    };
    const handleCloseCreate = () => {
        setOpenCreate(false);
    };
    const handleOpenEdit = (id) => {
        setId(id);
        setOpenEdit(true);
    };
    const handleCloseEdit = () => {
        setOpenEdit(false);
    };
    const handleOpenDelete = (id) => {
        setId(id);
        setOpenDelete(true);
    };
    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleCloseFeed = () => {
        setFeed((prevState) => ({ ...prevState, open: false }));
    };

    const emptyRows =
        rowsPerPage -
        Math.min(
            rowsPerPage,
            (Array(1000).length || filteredRows().length) - page * rowsPerPage
        );
        
    return (
        <div>
            {feed.success ? (
                <Feedback
                    handleCloseFeed={handleCloseFeed}
                    open={feed.open}
                    severity="success"
                    message={feed.message}
                />
            ) : (
                <Feedback
                    handleCloseFeed={handleCloseFeed}
                    open={feed.open}
                    severity="error"
                    message={feed.message}
                />
            )}
            <CreateItemModal {...{ openCreate, handleCloseCreate }} />
            <EditItemModal {...{ openEdit, handleCloseEdit, id }} />
            <DeleteItemModal {...{ openDelete, handleCloseDelete, id }} />

            <Typography variant="overline" gutterBottom className={classes.title}>
                Store / Inventory Items
            </Typography>
            <Grid
                container
                justify="space-between"
                alignItems="center"
                spacing={3}
                className={classes.marginBottom}
            >
                <Grid item>
                    <Button
                        color="primary"
                        variant="contained"
                        className={classes.createBtn}
                        onClick={handleOpenCreate}
                    >
                        Add Item
                    </Button>
                    <Grid item container justify="center" alignItems="center" style={{ marginTop: 20 }} >
                        <SearchComponent
                            updateSearch={updateSearch}
                            placeholder="Search..."
                            search={search}
                            ResetSearch={ResetSearch}
                        />
                    </Grid>
                </Grid>
                <Grid item style={{ alignContent: "center" }}>
                    <Grid container justify="center" alignItems="center" spacing={3}>
                        <Grid item style={{ width: 280 }}>
                            <FormControl fullWidth variant="outlined" required>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    inputProps={{
                                        id: "category",
                                        name: "category",
                                    }}
                                    value={category}
                                    onChange={handleChangeCategory}
                                >
                                    <MenuItem disabled>Select Category</MenuItem>
                                    {invCates.map((cate) => (
                                        <MenuItem key={cate._id} value={cate._id}>
                                            {cate.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Paper>
                <TableContainer>
                    <Table className={classes.table} aria-label="custom pagination table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Item Name</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Code</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Currency</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Total</TableCell>
                                <TableCell>Division</TableCell>
                                <TableCell>Edit</TableCell>
                                <TableCell>Delete</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(rowsPerPage > 0
                                ? filteredRows().slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage
                                )
                                : filteredRows()
                            ).map((row, i) => {
                                return (
                                    <TableRow hover key={row._id}>
                                        <TableCell component="th" scope="row">
                                            {row.name && row.name}
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {row.category?.name && row.category?.name}
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {row.code && row.code}
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {row.totalQuantity && row.totalQuantity} {" "}
                                            {row.unit && row.unit}
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            <Chip label={row.currency} variant="outlined" />
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {row.cost ? formatAmount(row.cost, "en-NG") : row.cost}
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {row.totalValue ? formatAmount(row.totalValue, "en-NG") : row.totalValue}
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {row.division && row.division?.name}
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleOpenEdit(row._id)}
                                                variant="extended"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            <IconButton
                                                color="secondary"
                                                onClick={() => handleOpenDelete(row._id)}
                                                variant="extended"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                            {filteredRows().length === 0 && (
                                <TableRow hover>
                                    <TableCell
                                        colSpan={10}
                                        align="center"
                                        style={{ color: "#616161" }}
                                        component="th"
                                        scope="row"
                                    >
                                        No Records To Display
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                    colSpan={3}
                    count={filteredRows().length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                        inputProps: { "aria-label": "rows per page" },
                        native: true,
                    }}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                />
            </Paper>
            <Backdrop className={classes.backdrop} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </div >
    );
};

export default InventoryItems;
